import React, { useEffect, useMemo, useState, useRef } from 'react'
import {
  resolvePrimaryColor,
  useWorkspaceBranding,
  withWorkspaceStyle,
} from 'src/utils/workspace-logo'
import { createRoot } from 'react-dom/client'
import { useCourseSettings } from 'src/hooks/useCourseSettings'
import { withStyles } from '@material-ui/core'
import styles from 'src/components/editor/components/ck-editor/styles'
import { useDispatch, useSelector } from 'react-redux'
import { createCourseTemplateAccordion } from 'src/components/editor/components/custom-plugins/simple-box/course-outline-template-accordion'
import { createCourseTemplate } from 'src/components/editor/components/custom-plugins/simple-box/course-outline-template'
import CreateCollectionList from 'src/components/editor/components/custom-plugins/simple-box/collection-list'
import CreateCourseList2 from 'src/components/editor/components/custom-plugins/simple-box/course-list2'
import CreateCourseList from 'src/components/editor/components/custom-plugins/simple-box/course-list'
import CreateBadgeList from 'src/components/editor/components/custom-plugins/simple-box/badge-list'
import CreateCatalogList, {
  initCatalogFilterEvents,
  setPageAccent,
} from 'src/components/editor/components/custom-plugins/simple-box/catalog'
import CreateContinueLearning, {
  initContinueLearningEvents,
} from 'src/components/editor/components/custom-plugins/simple-box/continue-learning'
import { editorEnum } from 'src/model/editor/editorEnum'
import CommunityBlock from '@minicoursegenerator/community-block'

const LandingPagePreview = props => {
  const {
    classes,
    fontFamily,
    fontSize,
    pageUrl,
    customHtmlContent,
    theme,
  } = props
  const { value } = useCourseSettings('general')
  const [editorData, setEditorData] = useState(null)
  const [processedContent, setProcessedContent] = useState('')
  const containerRef = useRef(null)
  const communityRootsRef = useRef([])
  // The continue learning popup needs the list the block was built from, and the block is
  // injected as plain HTML, so the items are parked here per block until the effect wires them.
  const continueLearningItemsRef = useRef([])

  const landingPageData = useSelector(state => state.course.landingPageSettings)
  const savedCollectionPage = useSelector(
    state => state.collection.landingPageSettings,
  )
  // A path or portal page on the defaults shows the workspace color and font,
  // as learners see it (courses also have their path in between, so they
  // keep their own values here).
  const workspaceBranding = useWorkspaceBranding()
  const landingPageDataCollection = useMemo(
    () => withWorkspaceStyle(savedCollectionPage, workspaceBranding),
    [savedCollectionPage, workspaceBranding],
  )
  // Course list, catalog and continue learning cards draw their progress rows in the page's
  // primary colour, read from --page-accent on the preview container.
  // A course on the default color shows the workspace branding's.
  const pagePrimaryColor = resolvePrimaryColor(
    (pageUrl === 'mini-course' ? landingPageData : landingPageDataCollection)
      ?.primaryColor,
  )
  const collectionCards = useSelector(state => state.collection)
  const collection = useSelector(state => state.collection.collection)
  const course = useSelector(state => state.course.course)
  const currentCard = useSelector(state => state.course.currentCard)
  const courseCards = useSelector(state => state.course.courseCards)
  const userInfo = useSelector(state => state.authentication?.me)

  // The empty catalog's icon and the "Your learning" link follow the page's primary colour.
  // While a preset is being
  // previewed (customHtmlContent) the preset picker has set its own colour, so it is left alone.
  const savedPage =
    pageUrl === 'mini-course' ? landingPageData : landingPageDataCollection
  const savedPrimaryColor = savedPage?.primaryColor
  // The page's own HTML (e.g. an AI-built page) is not a preset preview.
  const showsSavedPage =
    !customHtmlContent || customHtmlContent === savedPage?.html
  // Re-applied once the workspace branding loads: a page on the default color
  // shows the branding's (Default preset headings follow it too).
  useEffect(() => {
    if (showsSavedPage) setPageAccent(savedPrimaryColor)
  }, [savedPrimaryColor, showsSavedPage, workspaceBranding])

  useEffect(() => {
    if (value?.coverPageText) {
      setEditorData(value.coverPageText)
    }
  }, [value])
  const dispatch = useDispatch()

  // Initialize image carousels after HTML is rendered
  useEffect(() => {
    if (!containerRef.current || !processedContent) return

    const carouselWrappers = containerRef.current.querySelectorAll(
      '.image-carousel-wrapper',
    )

    carouselWrappers.forEach(wrapper => {
      const items = wrapper.querySelectorAll('.image-carousel-item')
      if (items.length === 0) return

      let currentIndex = 0

      // Set first item active
      items.forEach((item, i) => {
        if (i === 0) {
          item.classList.add('active')
        } else {
          item.classList.remove('active')
        }
      })

      const goTo = index => {
        currentIndex = ((index % items.length) + items.length) % items.length
        items.forEach((item, i) => {
          if (i === currentIndex) {
            item.classList.add('active')
          } else {
            item.classList.remove('active')
          }
        })
        const allDots = wrapper.querySelectorAll('.image-carousel-editor-dot')
        allDots.forEach((dot, i) => {
          if (i === currentIndex) {
            dot.classList.add('active')
          } else {
            dot.classList.remove('active')
          }
        })
      }

      // Navigation comes from CKEditor data output - just bind event listeners
      const prevBtn = wrapper.querySelector('.carousel-prev')
      const nextBtn = wrapper.querySelector('.carousel-next')
      if (prevBtn)
        prevBtn.addEventListener('click', () => goTo(currentIndex - 1))
      if (nextBtn)
        nextBtn.addEventListener('click', () => goTo(currentIndex + 1))
      wrapper
        .querySelectorAll('.image-carousel-editor-dot')
        .forEach((dot, i) => {
          dot.addEventListener('click', () => goTo(i))
        })
    })
  }, [processedContent])

  // The catalog's search box and content-type checkboxes are plain DOM, so they need wiring
  // once the preview HTML lands.
  useEffect(() => {
    if (!containerRef.current || !processedContent) return

    const catalogs = containerRef.current.querySelectorAll(
      '[data-type="catalog"]',
    )

    catalogs.forEach(element => {
      initCatalogFilterEvents(element, {
        catalogId: element.getAttribute('data-catalog-id') || '',
        priceTagActive:
          element.getAttribute('data-price-tag-active') === 'true',
      })
    })
  }, [processedContent])

  // The "Your learning" link is plain DOM like the catalog's filters, so it is wired once
  // the preview HTML lands, with the items the matching block was built from.
  useEffect(() => {
    if (!containerRef.current || !processedContent) return

    const blocks = containerRef.current.querySelectorAll(
      '[data-type="continueLearning"]',
    )

    blocks.forEach((element, index) => {
      if (element.getAttribute('data-cl-hidden') === 'true') {
        const section =
          element.closest('.simple-box-wrapper') ||
          element.closest('.simple-box') ||
          element
        section.style.display = 'none'
        return
      }

      initContinueLearningEvents(element, {
        items: continueLearningItemsRef.current[index] || [],
      })
    })
  }, [processedContent])

  // Mount CommunityBlock components after HTML is rendered
  useEffect(() => {
    if (!containerRef.current || !processedContent) return

    // Cleanup previous roots
    communityRootsRef.current.forEach(root => {
      try {
        root.unmount()
      } catch (e) {
        console.error('Error unmounting community block:', e)
      }
    })
    communityRootsRef.current = []

    // Find all community-block divs
    const communityBlocks = containerRef.current.querySelectorAll(
      '[data-type="community-block"]',
    )

    communityBlocks.forEach(element => {
      const communityId = element.getAttribute('data-community-id')
      const dataAttr = element.getAttribute('data')
      let parsedData = {}

      try {
        if (dataAttr) {
          parsedData = JSON.parse(dataAttr)
        }
      } catch (e) {
        console.error('Error parsing community block data:', e)
      }

      // Determine if mini-course or collection
      const isMiniCourse = pageUrl === 'mini-course'
      const isCollection = pageUrl === 'collection'

      // Get community language - default to course/collection language or 'en'
      const communityLanguage =
        element.getAttribute('data-community-language') ||
        (isMiniCourse ? course?.language : collection?.language) ||
        'en'

      const props = {
        communityId: communityId || parsedData.id,
        courseId: course?.id,
        collectionId: collection?.id,
        courseCode: isMiniCourse ? course?.code : collection?.code,
        courseName: isMiniCourse ? course?.name : null,
        collectionName: isCollection ? collection?.name : null,
        isLandingPage: true,
        isMiniCourse,
        isCollection,
        userInfo,
        language: communityLanguage,
        environment: process.env.REACT_APP_ENVIRONMENT || 'test',
      }

      console.log('🟢 LandingPagePreview - CommunityBlock Props:', props)

      const root = createRoot(element)
      root.render(React.createElement(CommunityBlock, props))
      communityRootsRef.current.push(root)
    })

    // Cleanup on unmount
    return () => {
      communityRootsRef.current.forEach(root => {
        try {
          root.unmount()
        } catch (e) {
          console.error('Error unmounting community block:', e)
        }
      })
      communityRootsRef.current = []
    }
  }, [processedContent, course, collection, pageUrl, userInfo])

  // Process dynamic lists
  useEffect(() => {
    const getContentToDisplay = () => {
      // First priority: use customHtmlContent if it exists and isn't empty
      if (customHtmlContent && customHtmlContent !== '') {
        return customHtmlContent
      }

      // Second priority: use appropriate landing page data based on pageUrl
      if (pageUrl === 'mini-course') {
        const landingPageSection = landingPageData?.sections?.find(
          x => x.type === editorEnum.mainType,
        )
        return landingPageSection?.content?.html || ''
      } else {
        const landingPageSection = landingPageDataCollection?.sections?.find(
          x => x.type === editorEnum.mainType,
        )
        return landingPageSection?.content?.html || ''
      }
    }

    const processDynamicLists = async () => {
      let html = getContentToDisplay()

      if (!html || !html.includes('dynamic-list')) {
        setProcessedContent(html)
        return
      }

      // Get landing page settings based on pageUrl
      const currentLandingPageData =
        pageUrl === 'mini-course' ? landingPageData : landingPageDataCollection

      const landingPageSettings = {
        primaryColor: currentLandingPageData?.primaryColor || '#4CAF50',
        font: currentLandingPageData?.font || 'Inter',
        localizations: {
          startButton:
            currentLandingPageData?.localizations?.startButton ||
            'START COURSE',
          quiz: currentLandingPageData?.localizations?.quiz || 'Quiz',
          feedback:
            currentLandingPageData?.localizations?.feedback || 'Feedback',
        },
      }

      // courseList replacement (synchronous)
      const courseListRegex = /<div\s+([^>]*\bclass=["'][^"']*dynamic-list[^"']*["'][^>]*\bdata-type=["']courseList1["'][^>]*)>([\s\S]*?)<\/div>/gi

      html = html.replace(
        courseListRegex,
        (match, attributes, innerContent) => {
          try {
            const priceTagMatch = attributes.match(
              /data-price-tag-active=["']([^"']*)["']/,
            )
            const isPriceTagActive = priceTagMatch?.[1] === 'true'
            const isEdit = false
            const content = CreateCourseList(
              collectionCards.courses,
              dispatch,
              isEdit,
              isPriceTagActive,
            )
            return `<div ${attributes}>${content}</div>`
          } catch (error) {
            console.error('Error creating course list:', error)
            return match
          }
        },
      )

      // courseList2 replacement (synchronous) - compact version
      const courseList2Regex = /<div\s+([^>]*\bclass=["'][^"']*dynamic-list[^"']*["'][^>]*\bdata-type=["']courseList2["'][^>]*)>([\s\S]*?)<\/div>/gi

      html = html.replace(
        courseList2Regex,
        (match, attributes, innerContent) => {
          try {
            const priceTagMatch = attributes.match(
              /data-price-tag-active=["']([^"']*)["']/,
            )
            const isPriceTagActive = priceTagMatch?.[1] === 'true'
            const isEdit = false
            const content = CreateCourseList2(
              collectionCards.courses,
              dispatch,
              isEdit,
              isPriceTagActive,
            )
            return `<div ${attributes}>${content}</div>`
          } catch (error) {
            console.error('Error creating course list 2:', error)
            return match
          }
        },
      )

      // courseOutline1 için regex
      const courseOutline1Regex = /<div\s+([^>]*\bclass=["'][^"']*dynamic-list[^"']*["'][^>]*\bdata-type=["']courseOutline1["'][^>]*)>([\s\S]*?)<\/div>/gi

      const matches1 = [...html.matchAll(courseOutline1Regex)]

      html = html.replace(
        courseOutline1Regex,
        (match, attributes, innerContent) => {
          if (!course) {
            console.warn('Course data not found')
            return match
          }

          try {
            const priceTagMatch = attributes.match(
              /data-price-tag-active=["']([^"']*)["']/,
            )
            const display = 'none'
            const isPriceTagActive = priceTagMatch?.[1] === 'true'
            const content = createCourseTemplate(
              courseCards,
              currentLandingPageData,
              display,
              isPriceTagActive,
            )
            return `<div ${attributes}>${content}</div>`
          } catch (error) {
            console.error('Error creating course template:', error)
            return match
          }
        },
      )

      const courseOutline2Regex = /<div([^>]*class=["'][^"']*dynamic-list[^"']*["'][^>]*\bdata-type=["']courseOutline2["'][^>]*)>([\s\S]*?)<\/div>/gi

      const matches2 = [...html.matchAll(courseOutline2Regex)]

      html = html.replace(
        courseOutline2Regex,
        (match, attributes, innerContent) => {
          if (!course) {
            console.warn('Course data not found')
            return match
          }

          try {
            const priceTagMatch = attributes.match(
              /data-price-tag-active=["']([^"']*)["']/,
            )
            const isPriceTagActive = priceTagMatch?.[1] === 'true'
            const display = 'none'
            const content = createCourseTemplateAccordion(
              courseCards,
              currentLandingPageData,
              display,
              isPriceTagActive,
            )
            return `<div ${attributes}>${content}</div>`
          } catch (error) {
            console.error('Error creating course template accordion:', error)
            return match
          }
        },
      )

      const collectionListRegex = /<div\s+([^>]*\bclass=["'][^"']*dynamic-list[^"']*["'][^>]*\bdata-type=["']collectionList["'][^>]*)>([\s\S]*?)<\/div>/gi

      const matches3 = [...html.matchAll(collectionListRegex)]

      for (const match of matches3) {
        const [fullMatch, attributes] = match

        const collectionIdsMatch = attributes.match(
          /data-collections=["']([^"']*)["']/,
        )
        const gridMatch = attributes.match(/data-grid=["']([^"']*)["']/)
        const priceTagMatch = attributes.match(
          /data-price-tag-active=["']([^"']*)["']/,
        )

        try {
          const collectionIds = collectionIdsMatch?.[1] || ''
          const priceTagActive = priceTagMatch?.[1] === 'true'
          const isEdit = false

          const content = await CreateCollectionList(
            dispatch,
            isEdit,
            collectionIds,
            priceTagActive,
          )

          html = html.replace(fullMatch, `<div ${attributes}>${content}</div>`)
        } catch (error) {
          console.error('Error creating collection list:', error)
          html = html.replace(
            fullMatch,
            `<div ${attributes}><p>Learning path list will be displayed here</p></div>`,
          )
        }
      }

      // Catalog replacement (async)
      const catalogRegex = /<div\s+([^>]*\bclass=["'][^"']*dynamic-list[^"']*["'][^>]*\bdata-type=["']catalog["'][^>]*)>([\s\S]*?)<\/div>/gi

      const catalogMatches = [...html.matchAll(catalogRegex)]

      for (const match of catalogMatches) {
        const [fullMatch, attributes] = match

        const gridMatch = attributes.match(/data-grid=["']([^"']*)["']/)
        const priceTagMatch = attributes.match(
          /data-price-tag-active=["']([^"']*)["']/,
        )
        const catalogIdMatch = attributes.match(
          /data-catalog-id=["']([^"']*)["']/,
        )

        try {
          const content = await CreateCatalogList(
            gridMatch?.[1] || '3',
            priceTagMatch?.[1] === 'true',
            catalogIdMatch?.[1] || '',
            { live: true },
          )

          html = html.replace(fullMatch, `<div ${attributes}>${content}</div>`)
        } catch (error) {
          console.error('Error creating catalog:', error)
          html = html.replace(
            fullMatch,
            `<div ${attributes}><p>Catalog will be displayed here</p></div>`,
          )
        }
      }

      // Continue learning replacement (async)
      const continueLearningRegex = /<div\s+([^>]*\bclass=["'][^"']*dynamic-list[^"']*["'][^>]*\bdata-type=["']continueLearning["'][^>]*)>([\s\S]*?)<\/div>/gi

      const continueLearningMatches = [...html.matchAll(continueLearningRegex)]
      continueLearningItemsRef.current = []

      for (const match of continueLearningMatches) {
        const [fullMatch, attributes] = match

        const gridMatch = attributes.match(/data-grid=["']([^"']*)["']/)
        const priceTagMatch = attributes.match(
          /data-price-tag-active=["']([^"']*)["']/,
        )
        const titleMatch = attributes.match(/data-cl-title=["']([^"']*)["']/)
        const linkLabelMatch = attributes.match(
          /data-cl-link-label=["']([^"']*)["']/,
        )
        const titleColorMatch = attributes.match(
          /data-cl-title-color=["']([^"']*)["']/,
        )

        try {
          const { html: content, items, hidden } = await CreateContinueLearning(
            gridMatch?.[1] || '3',
            priceTagMatch?.[1] === 'true',
            {
              title: titleMatch?.[1] || undefined,
              linkLabel: linkLabelMatch?.[1] || undefined,
              titleColor: titleColorMatch?.[1] || '',
              live: true,
            },
          )

          continueLearningItemsRef.current.push(items)
          // As in the app, a block with nothing in progress is not shown; its section is hidden
          // once the preview lands so the section's own padding does not leave a gap.
          html = html.replace(
            fullMatch,
            hidden
              ? `<div ${attributes} data-cl-hidden="true"></div>`
              : `<div ${attributes}>${content}</div>`,
          )
        } catch (error) {
          console.error('Error creating continue learning:', error)
          continueLearningItemsRef.current.push([])
          html = html.replace(
            fullMatch,
            `<div ${attributes}><p>Continue learning will be displayed here</p></div>`,
          )
        }
      }

      // Badge list replacement (async)
      const badgeListRegex = /<div\s+([^>]*\bclass=["'][^"']*dynamic-list[^"']*["'][^>]*\bdata-type=["']badge["'][^>]*)>([\s\S]*?)<\/div>/gi

      const badgeMatches = [...html.matchAll(badgeListRegex)]

      for (const match of badgeMatches) {
        const [fullMatch, attributes] = match

        const rewardBgMatch = attributes.match(
          /data-completion-reward-bg-color=["']([^"']*)["']/,
        )
        const headerTitleMatch = attributes.match(
          /data-badge-header-title=["']([^"']*)["']/,
        )
        const headerIconMatch = attributes.match(
          /data-badge-header-icon=["']([^"']*)["']/,
        )
        const startTextMatch = attributes.match(/data-start=["']([^"']*)["']/)

        try {
          const collectionId = collection?.id || ''
          const rewardBgColor =
            rewardBgMatch?.[1] ||
            'linear-gradient(90deg, #004483 0%, #001C36 100%)'
          const headerTitle = headerTitleMatch?.[1] || ''
          const headerIcon = headerIconMatch?.[1] || ''
          const startText = startTextMatch?.[1] || 'Start'

          const content = await CreateBadgeList(
            collectionId,
            rewardBgColor,
            headerTitle,
            headerIcon,
            startText,
            true,
          )

          html = html.replace(fullMatch, `<div ${attributes}>${content}</div>`)
        } catch (error) {
          console.error('Error creating badge list:', error)
        }
      }

      setProcessedContent(html)
    }

    processDynamicLists()
  }, [
    customHtmlContent,
    landingPageData,
    landingPageDataCollection,
    collectionCards,
    collection,
    course,
    currentCard,
    pageUrl,
    dispatch,
    courseCards,
  ])

  return (
    <div className={classes.ckEditorFont}>
      <div
        ref={containerRef}
        className="mcg-landing-content"
        style={
          pagePrimaryColor ? { '--page-accent': pagePrimaryColor } : undefined
        }
        fontSize={fontSize}
        fontFamily={fontFamily}
        theme={theme}
        id="ck-editor-font"
        dangerouslySetInnerHTML={{
          __html: processedContent,
        }}
      />
    </div>
  )
}

export default withStyles(styles)(LandingPagePreview)
